import { useMutation } from '@apollo/client';
import { compatibility } from '@seek/ca-myprofile-utils';
import { metrics } from '@seek/metrics-js';
import { useLanguage, useTranslations } from '@vocab/react';
import { useEffect } from 'react';

import { browserLogError } from 'src/modules/error-logger/browserLog';
import { updateCTPSubscriptionQL } from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import type {
  UpdateNotificationCtpSubscriptionWithTokenMutation,
  UpdateNotificationCtpSubscriptionWithTokenMutationVariables,
} from 'src/types/graphql';

import Subscription from '../common/subscription';
import PageLoader from '../page-loader';
import { trackUnsubscribeSuccessPageLoad } from '../tracking/tealium';

import translations from './.vocab';
import { UnsubscribeIcon } from './svg/unsubscribe-icon';
import UnsubscribeError from './unsubscribe-error';

const Unsubscribe = ({ token }: { token: string }) => {
  const { language: vocabLanguage } = useLanguage();
  const { t } = useTranslations(translations);
  const language =
    compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);

  const [unsubscribeCTPMutation, { error, data }] = useMutation<
    UpdateNotificationCtpSubscriptionWithTokenMutation,
    UpdateNotificationCtpSubscriptionWithTokenMutationVariables
  >(updateCTPSubscriptionQL);

  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  );
  const zone = useTypedSelector((state) => state.page.pageAnalytics.zone);
  const locale = `${language}-${country.toUpperCase()}`;
  const isGraphFailure =
    data?.updateNotificationCtpSubscriptionWithToken?.__typename ===
    'UpdateNotificationCtpPreferenceFailure';

  useEffect(() => {
    if (token) {
      unsubscribeCTPMutation({
        variables: {
          input: {
            token,
            subscribe: false,
            locale,
          },
          locale,
        },
      }).catch((e) => browserLogError(e));
    }
  }, [unsubscribeCTPMutation, token, language, locale]);

  if (error || isGraphFailure) {
    metrics.count('unsubscribe-error-page-rendered');
    return <UnsubscribeError language={language} zone={zone} />;
  }

  if (
    data?.updateNotificationCtpSubscriptionWithToken?.__typename ===
    'UpdateNotificationCtpSubscriptionWithTokenSuccess'
  ) {
    const result = data.updateNotificationCtpSubscriptionWithToken;
    const product = result.product.id;
    const channel = result.subscriptionChannel.id;
    const productName = result.product.label.toLowerCase() ?? '';
    trackUnsubscribeSuccessPageLoad(product, channel);
    metrics.count('unsubscribe-page-rendered');

    return (
      <Subscription
        title={t('default-catchall-heading', {
          PreferenceCentreEmailTitle: productName,
        })}
        cardTitle={t('unsubscribe-subscription-card-title')}
        body={t('default-catchall-message')}
        body2={t('default-catchall-message-2')}
        linkMessage={t('unsubscribe-subscription-link-message')}
        svg={<UnsubscribeIcon />}
      />
    );
  }

  return <PageLoader />;
};

export default Unsubscribe;
